import React, { useRef, useState } from 'react';
import { propertiesAPI } from '../lib/api';
import { useTranslation } from '../lib/i18n';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  X, Upload, Loader2, Plus, Image as ImageIcon, Trash2,
  Link as LinkIcon, Pencil, Users,
} from 'lucide-react';
import WillhabenImportFlow from './WillhabenImportFlow';
import WillhabenBrokerImportFlow from './WillhabenBrokerImportFlow';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Modal for manually creating a property. Replaces the old full-page
 * `/properties/new` route.
 *
 * Why a modal: most users come through Justimmo (95%+). The full-page
 * form felt heavy for the rare manual-add case. Modal communicates
 * "small task, you're not leaving the page" and surfaces the file-
 * picker UX prominently rather than burying it behind URL paste.
 *
 * Drag-drop + multi-file picker via the `/properties/upload/image`
 * endpoint. Each upload returns a public URL we collect into `images`.
 * URL paste stays as a secondary path under the "Eigene URLs" expander
 * for users with images already hosted elsewhere.
 *
 * Props
 *   onClose()  . Called when user dismisses (X, backdrop, or Escape)
 *   onCreated(). Called after successful POST /api/properties
 */
export default function NewPropertyModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    location: '',
    highlights: [''],
    images: [],
  });
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [showUrlPaste, setShowUrlPaste] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  // Tab toggle:
  //   'broker'   → bulk-import all listings from a willhaben Anbieter
  //   'willhaben' → single-listing willhaben import (paste detail URL)
  //   'manual'   → fill the form yourself with custom photos
  //
  // Default = 'broker'. For a Makler the highest-value path is
  // "import everything I already have on willhaben", and the broker
  // flow handles the single-listing case naturally too (just pick one
  // from the resulting list).
  const [tab, setTab] = useState('broker');
  // When the user single-imports a listing, we pop a "import more from
  // this broker?" prompt. If they say yes we route them into the
  // broker tab with this URL pre-pasted so they don't have to find it
  // again. Setting this triggers a remount of WillhabenBrokerImportFlow
  // via the `key` prop so its internal URL state initialises fresh.
  const [brokerSeedUrl, setBrokerSeedUrl] = useState(null);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  // Upload one or many files in parallel. Each succeeds independently;
  // a single failure surfaces a toast but doesn't block the others.
  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    const allowed = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif']);
    setUploading(true);
    const token = localStorage.getItem('auth_token');
    const headers = { Authorization: `Bearer ${token}` };
    const gatewayKey = process.env.REACT_APP_GATEWAY_KEY;
    if (gatewayKey) headers['X-Gateway-Key'] = gatewayKey;

    const newUrls = [];
    for (const file of Array.from(files)) {
      if (!allowed.has(file.type)) {
        toast.error(`${file.name}: ${t('imageUpload.invalidType') || 'Falscher Dateityp'}`);
        continue;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name}: ${t('imageUpload.tooLarge') || 'Datei zu groß (max 5MB)'}`);
        continue;
      }
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await fetch(`${API_BASE}/api/properties/upload/image`, {
          method: 'POST',
          headers,
          body: fd,
        });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.detail || 'upload failed');
        }
        const data = await res.json();
        newUrls.push(data.url);
      } catch (e) {
        toast.error(`${file.name}: ${e.message}`);
      }
    }
    if (newUrls.length) {
      set({ images: [...form.images, ...newUrls] });
      // i18n now handles pluralization via Intl.PluralRules. The
      // translation entry is `{ one: 'Bild hochgeladen', other:
      // '{{count}} Bilder hochgeladen' }`, t() picks the right form
      // based on params.count.
      toast.success(t('newProperty.imagesUploaded', { count: newUrls.length }));
    }
    setUploading(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) {
      uploadFiles(e.dataTransfer.files);
    }
  };

  const handlePaste = () => {
    const trimmed = urlInput.trim();
    if (!trimmed) return;
    set({ images: [...form.images, trimmed] });
    setUrlInput('');
  };

  const removeImage = (i) =>
    set({ images: form.images.filter((_, idx) => idx !== i) });

  const updateHighlight = (i, value) => {
    const next = [...form.highlights];
    next[i] = value;
    set({ highlights: next });
  };
  const addHighlight = () => set({ highlights: [...form.highlights, ''] });
  const removeHighlight = (i) =>
    set({ highlights: form.highlights.filter((_, idx) => idx !== i) });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.price || !form.location) {
      toast.error(t('newProperty.fillRequired') || 'Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    if (form.images.length === 0) {
      toast.error(t('newProperty.imagesRequired') || 'Mindestens ein Bild ist erforderlich.');
      return;
    }
    setSubmitting(true);
    try {
      await propertiesAPI.create({
        title: form.title,
        description: form.description,
        price: form.price,
        location: form.location,
        highlights: form.highlights.filter(h => h.trim()),
        images: form.images,
      });
      toast.success(t('newProperty.created') || 'Immobilie erstellt.');
      onCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('newProperty.createFailed') || 'Erstellen fehlgeschlagen.');
    } finally {
      setSubmitting(false);
    }
  };

  // Close on Escape. Same UX users expect from any modal.
  React.useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="glass border-2 border-[#1f4d3a]/30 w-full sm:max-w-2xl max-h-[92dvh] sm:max-h-[90dvh] overflow-y-auto overscroll-contain rounded-t-2xl sm:rounded-2xl">
        {/* Sticky header so the close button stays reachable while
            scrolling a long form. */}
        <div className="sticky top-0 bg-[#fffdf7]/95 backdrop-blur-md border-b border-[#e3dccb] px-5 sm:px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-[#23211a]">
            {t('newProperty.title') || 'Neue Immobilie'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-[#efe9db] text-[#6f6a5c] hover:text-[#23211a] transition"
            aria-label={t('common.close') || 'Close'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab toggle: three modes. Anbieter (broker bulk-import) is
            the default since it's the highest-value path for a Makler.
            Single-Inserat (paste one URL) and Manuell (fill the form)
            stay accessible for the other paths. */}
        <div className="px-5 sm:px-6 pt-5">
          <div className="inline-flex bg-white border border-[#e3dccb] rounded-full p-1 gap-1 w-full sm:w-auto flex-wrap">
            <button
              type="button"
              onClick={() => setTab('broker')}
              className={`flex-1 sm:flex-initial text-xs sm:text-sm px-3 py-1.5 rounded-full transition ${
                tab === 'broker'
                  ? 'bg-[#1f4d3a] text-white shadow'
                  : 'text-[#6f6a5c] hover:text-[#23211a]'
              }`}
              data-testid="new-property-tab-broker"
            >
              <Users className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
              {t('newProperty.tabBroker') || 'Anbieter-Profil'}
            </button>
            <button
              type="button"
              onClick={() => setTab('willhaben')}
              className={`flex-1 sm:flex-initial text-xs sm:text-sm px-3 py-1.5 rounded-full transition ${
                tab === 'willhaben'
                  ? 'bg-[#1f4d3a] text-white shadow'
                  : 'text-[#6f6a5c] hover:text-[#23211a]'
              }`}
              data-testid="new-property-tab-willhaben"
            >
              <LinkIcon className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
              {t('newProperty.tabWillhaben') || 'Einzelnes Inserat'}
            </button>
            <button
              type="button"
              onClick={() => setTab('manual')}
              className={`flex-1 sm:flex-initial text-xs sm:text-sm px-3 py-1.5 rounded-full transition ${
                tab === 'manual'
                  ? 'bg-[#1f4d3a] text-white shadow'
                  : 'text-[#6f6a5c] hover:text-[#23211a]'
              }`}
              data-testid="new-property-tab-manual"
            >
              <Pencil className="inline w-3.5 h-3.5 mr-1.5 -mt-0.5" />
              {t('newProperty.tabManual') || 'Manuell'}
            </button>
          </div>
        </div>

        {tab === 'broker' && (
          <div className="px-5 sm:px-6 py-5">
            <WillhabenBrokerImportFlow
              key={brokerSeedUrl || 'fresh'}
              seedUrl={brokerSeedUrl}
              variant="embedded"
              onCompleted={() => {
                // Bulk job finished. Refresh parent's list.
                onCreated?.();
              }}
            />
          </div>
        )}

        {tab === 'willhaben' && (
          <div className="px-5 sm:px-6 py-5">
            <WillhabenImportFlow
              variant="embedded"
              onImported={() => {
                // Property was already saved server-side by the import
                // endpoint (with locally-mirrored images). Tell the
                // parent + close.
                onCreated?.();
              }}
              onImportMoreFromBroker={(orgId) => {
                // User wants to bulk-import the rest of this broker's
                // listings. Switch to the broker tab and pre-paste the
                // canonical Anbieter URL.
                const brokerUrl = `https://www.willhaben.at/iad/immobilien/immobilienmakler?orgId=${orgId}`;
                setBrokerSeedUrl(brokerUrl);
                setTab('broker');
              }}
            />
          </div>
        )}

        {tab === 'manual' && (
        <form onSubmit={handleSubmit} className="px-5 sm:px-6 py-5 space-y-5">
          {/* Image picker. Primary CTA. Drag/drop + click to open
              system file picker. Multi-select supported. */}
          <div>
            <label className="text-sm font-medium text-[#23211a] block mb-2">
              {t('newProperty.imagesLabel') || 'Bilder'} *
            </label>
            <div
              className={`relative border-2 border-dashed rounded-xl text-center p-5 transition-all cursor-pointer ${
                dragOver
                  ? 'border-[#1f4d3a] bg-[#e9efe7]'
                  : 'border-[#d8d0bd] hover:border-[#1f4d3a]/40 bg-[#f7f3ea]/30'
              } ${uploading ? 'pointer-events-none opacity-60' : ''}`}
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                multiple
                onChange={(e) => uploadFiles(e.target.files)}
                className="hidden"
                data-testid="new-property-image-input"
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-2 py-3">
                  <Loader2 className="w-7 h-7 text-[#1f4d3a] animate-spin" />
                  <span className="text-sm text-[#6f6a5c]">
                    {t('newProperty.uploading') || 'Wird hochgeladen…'}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2 py-3">
                  <div className="p-2.5 bg-[#e9e2d2] rounded-full">
                    <Upload className="w-5 h-5 text-[#4a463b]" />
                  </div>
                  <p className="text-sm text-[#23211a]">
                    {t('newProperty.dragOrClick') || 'Bilder hierhin ziehen oder klicken'}
                  </p>
                  <p className="text-xs text-[#8a8474]">
                    {t('newProperty.imageHint') || 'JPG, PNG, WebP, GIF · max 5MB pro Datei · mehrere möglich'}
                  </p>
                </div>
              )}
            </div>

            {/* Thumbnails of staged images. First one is the cover. */}
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-3">
                {form.images.map((url, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-[#fffdf7] border border-[#e3dccb] group">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    {i === 0 && (
                      <span className="absolute top-1 left-1 text-[10px] px-1.5 py-0.5 rounded bg-[#1f4d3a] text-white">
                        {t('newProperty.coverBadge') || 'Cover'}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); removeImage(i); }}
                      className="absolute top-1 right-1 p-1 rounded-full bg-black/70 hover:bg-[#a34430] transition opacity-0 group-hover:opacity-100"
                      aria-label="Remove image"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* URL paste fallback. Collapsed by default so the file
                picker stays the prominent affordance. */}
            <button
              type="button"
              onClick={() => setShowUrlPaste((v) => !v)}
              className="text-xs text-[#6f6a5c] hover:text-[#23211a] mt-3 underline"
            >
              {showUrlPaste
                ? t('newProperty.hideUrlPaste') || 'URL-Eingabe ausblenden'
                : t('newProperty.showUrlPaste') || 'Oder Bild-URLs einfügen'}
            </button>
            {showUrlPaste && (
              <div className="flex gap-2 mt-2">
                <Input
                  type="url"
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://..."
                  className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] flex-1"
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handlePaste(); } }}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={handlePaste}
                  className="border-[#d8d0bd] text-[#23211a] shrink-0"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="text-sm font-medium text-[#23211a] block mb-1.5">
              {t('newProperty.titleLabel') || 'Titel'} *
            </label>
            <Input
              type="text"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder={t('newProperty.titlePlaceholder') || 'Schönes Familienhaus in Wien'}
              className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
              required
              data-testid="new-property-title"
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-sm font-medium text-[#23211a] block mb-1.5">
              {t('newProperty.descriptionLabel') || 'Beschreibung'} *
            </label>
            <textarea
              value={form.description}
              onChange={(e) => set({ description: e.target.value })}
              rows={3}
              placeholder={t('newProperty.descriptionPlaceholder') || 'Charmante Immobilie in begehrter Lage…'}
              className="w-full bg-[#f7f3ea]/85 border border-[#e3dccb] rounded-md px-3 py-2 text-sm text-[#23211a] focus:outline-none focus:border-[#1f4d3a] focus:ring-2 focus:ring-[#1f4d3a]/20"
              required
              data-testid="new-property-description"
            />
          </div>

          {/* Price + location side-by-side on tablet+ */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                {t('newProperty.priceLabel') || 'Preis'} *
              </label>
              <Input
                type="text"
                value={form.price}
                onChange={(e) => set({ price: e.target.value })}
                placeholder="€ 450.000"
                className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
                required
                data-testid="new-property-price"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-[#23211a] block mb-1.5">
                {t('newProperty.locationLabel') || 'Adresse'} *
              </label>
              <Input
                type="text"
                value={form.location}
                onChange={(e) => set({ location: e.target.value })}
                placeholder="1010 Wien"
                className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a]"
                required
                data-testid="new-property-location"
              />
            </div>
          </div>

          {/* Highlights. Dynamic list */}
          <div>
            <label className="text-sm font-medium text-[#23211a] block mb-1.5">
              {t('newProperty.highlightsLabel') || 'Highlights'}
            </label>
            <div className="space-y-2">
              {form.highlights.map((h, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    type="text"
                    value={h}
                    onChange={(e) => updateHighlight(i, e.target.value)}
                    placeholder={t('newProperty.highlightPlaceholder') || 'z.B. 4 Zimmer · 130 m² · Garten'}
                    className="bg-[#f7f3ea]/85 border-[#e3dccb] focus:border-[#1f4d3a] flex-1"
                  />
                  {form.highlights.length > 1 && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => removeHighlight(i)}
                      className="border-[#d8d0bd] text-[#6f6a5c] shrink-0"
                      aria-label={t('common.remove') || 'Entfernen'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button
                type="button"
                variant="ghost"
                onClick={addHighlight}
                className="text-[#1f4d3a] hover:text-[#1f4d3a] hover:bg-[#e9efe7]"
              >
                <Plus className="w-4 h-4 mr-1.5" />
                {t('newProperty.addHighlight') || 'Highlight hinzufügen'}
              </Button>
            </div>
          </div>

          {/* Action row */}
          <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-[#d8d0bd] text-[#4a463b] sm:flex-1"
              disabled={submitting || uploading}
            >
              {t('common.cancel') || 'Abbrechen'}
            </Button>
            <Button
              type="submit"
              className="bg-[#1f4d3a] hover:bg-[#163a2c] text-white glow-primary sm:flex-1"
              disabled={submitting || uploading}
              data-testid="new-property-submit"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('common.loading') || 'Wird gespeichert…'}</>
              ) : (
                <>
                  <ImageIcon className="w-4 h-4 mr-2" />
                  {t('newProperty.create') || 'Immobilie erstellen'}
                </>
              )}
            </Button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}
